import { CustomWindow } from '@src/@types/window';
import {
  NativeMobileAppMessages,
  sendJsBridgeEventMessage,
} from '@src/helpers/js-event-bridge.helper';

export const SERVER_CONFIG_ELEMENT_ID = 'hc-pet-insurance__server-config';

export default class WindowService {
  static get document(): Document {
    return window.document;
  }

  static get window(): CustomWindow {
    return window as unknown as CustomWindow;
  }

  /**
   * If you're thinking about exposing this publicly, don't. The right way to
   * incorporate the proxy base path into a path (or reference its value at all)
   * is through the `useRouteUtils` hook. This exists only because
   * WindowService, as an ES6 class, can't consume hooks.
   */
  static proxyBasePath: string;
  static {
    this.proxyBasePath =
      document.getElementById(SERVER_CONFIG_ELEMENT_ID)?.dataset
        ?.proxyBasePath || '';
  }

  private static buildRedirectPath(path: string): string {
    const isInternalResource = path.startsWith(`${this.base}/`);

    let newPath = path;
    if (isInternalResource) {
      // Respect the proxy base path for redirects to internal routes to ensure
      // we don't send the user away from this particular deployment of the app,
      // if it's being hosted under a base path by a reverse proxy.
      newPath = `${this.proxyBasePath}${path}`;
    }
    return newPath;
  }

  public static readonly base = '/pet-insurance';

  static createDynamicScript(args: any) {
    const script = WindowService.document.createElement('script');
    const { id, src, async, onload, onerror } = args;

    script.id = id;
    script.src = src;
    script.async = async;
    script.onload = onload;
    script.onerror = onerror;

    WindowService.document.body.appendChild(script);
  }

  static parseQueryString(): Record<string, string> {
    const urlSearchParams = new URLSearchParams(
      WindowService.window.location.search,
    );

    return Array.from(urlSearchParams.entries()).reduce(
      (acc: any, curr: [string, string]) => {
        const [key, value] = curr.map((s) => s.trim());

        if (!acc[key]) {
          acc[key] = value;
        }

        return acc;
      },
      {},
    );
  }

  static getQueryParamValues(queryParamName: string): string[] {
    const urlSearchParams = new URLSearchParams(
      WindowService.window.location.search,
    );

    return [...urlSearchParams.entries()]
      .filter(([name]) => name === queryParamName)
      .map(([, value]) => value);
  }

  /**
   * Navigates the user to the provided `path`. If `path` corresponds to an
   * internal resource, this will respect any reverse proxy base path that may
   * be in use in the current deployment.
   */
  static redirectTo(path: string) {
    this.window.location.href = this.buildRedirectPath(path);
  }

  /**
   * Replaces the user's window location to the provided `path`. If `path`
   * corresponds to an internal resource, this will respect any reverse proxy
   * base path that may be in use in the current deployment.
   */
  static replace(path: string) {
    this.window.location.replace(this.buildRedirectPath(path));
  }

  static goBack = () => {
    // We do not care if history.back() is invoked before or after the JsBridgeEventMessage is sent.
    // The purpose of the bride event message is to 'catch', and perform the redirect if the
    // history.back() method is not recognized (more specifically in our NMA for iOS and Android)
    this.window.history.back();
    sendJsBridgeEventMessage(NativeMobileAppMessages.BACK);
  };
}
